import { useContext, useState } from 'react';
import { Button, Form, Modal, OverlayTrigger } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { withTranslation } from "react-i18next";
import { FiDelete, FiLink2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { RegulationContext } from "../../../App";
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { LS_AUTHORITY_KEY } from "../../../core/constants/common";
import useHasPermission from "../../../core/hooks/useHasPermission";
import EmissionFactorService from '../../../core/service/EmissionFactorService';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';
import GHGEmissionService from '../../../core/service/GHGEmissionService';
import Utils from "../../../core/utilities/Utils";

const EmissionFactorLinker = ({ t, item, onSaveSuccess, fromDataPointPage, openEmissionFactorLink, unlinkEmissionFactor, disabled = false }) => {
  const [showModal, setShowModal] = useState(false);
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ?? "";
  const [emissionTableList, setEmissionTableList] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [emissionSources, setEmissionSources] = useState([]);
  const [selectedFactor, setSelectedFactor] = useState("");
  const [emissionFactors, setEmissionFactors] = useState([]);
  const [sourcesWithFactors, setSourcesWithFactors] = useState([]);
  const [validated, setValidated] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUnlinkId, setPendingUnlinkId] = useState(null);
  const { hasConfigUpdatePermission } = useHasPermission();
  const [linkFrom, setLinkFrom] = useState('company');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [versions, setVersions] = useState([]);
  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = lsData?.profile?.company?.id;

  const isInvalid = (val) => {
    return validated && !val;
  }

  const handleClick = () => {
    if (emissionTableList.length === 0) {
      getEmissionTable();
    }
    setShowModal(true);
    setValidated(false);
    resetForm();
    if (item.emissionLinked) {
      if (fromDataPointPage) {
        getLinkedEmissionDatapoint();
      } else {
        getLinkedEmissionMatric();
      }
    }
  };

  const handleClose = () => {
    setShowModal(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedTable("");
    setSelectedSource("");
    setSelectedFactor("");
    setSelectedVersion("");
    setLinkFrom('company');
    setEmissionSources([]);
    setEmissionFactors([]);
    setSourcesWithFactors([]);
    setVersions([]);
  };

  const getEmissionTable = () => {
    GHGEmissionService.getDataSet(glbRegulationId)
      .then((response) => {
        setEmissionTableList(response.data.data);
      })
      .catch((error) => { });
  };

  const loadVersions = (table, linkFromValue) => {
    if (!table || !fromDataPointPage) return;
    const fetchCompanyId = linkFromValue === 'company' ? companyId : null;
    GHGEmissionMasterDataService.listVersions(table, fetchCompanyId).then(res => {
      if (res.status === 200) {
        const versionsList = Array.isArray(res.data) ? res.data : [res.data].filter(Boolean);
        setVersions(versionsList);
      }
    }).catch(err => { });
  };

  const getEmissionSourcesByVersion = (table, versionId = null, callback = null) => {
    const versionToUse = versionId || selectedVersion;
    if (!versionToUse || !fromDataPointPage || !table) {
      setEmissionSources([]);
      setSourcesWithFactors([]);
      return Promise.resolve();
    }
    return EmissionFactorService.getEmissionSourcesByVersion({
      versionId: versionToUse,
      db: table,
      scope: item.scope ?? '',
      category: item.scopeCategory ?? '',
    }).then(res => {
      if (res.status === 200) {
        let data = [];
        if (Array.isArray(res.data)) {
          data = res.data;
        } else if (res.data?.data && Array.isArray(res.data.data)) {
          data = res.data.data;
        }

        const formattedSources = data.map(item => {
          const sourceName = item.source?.name || '';
          const factors = item.factors || [];
          return {
            value: sourceName,
            display: sourceName,
            sourceId: item.source?.id,
            factors: factors
          };
        });

        setEmissionSources(formattedSources);
        setSourcesWithFactors(formattedSources);
        
        if (callback && typeof callback === 'function') {
          callback(formattedSources);
        }
      }
    }).catch(err => {
      setEmissionSources([]);
      setSourcesWithFactors([]);
    });
  };

  const getEmissionSources = (table) => {
    if (!table) {
      setEmissionSources([]);
      return;
    }
    GHGEmissionService.getEmissionSources(table, item.scope, item.scopeCategory)
      .then((response) => {
        setEmissionSources(response.data || []);
      })
      .catch((error) => {
        setEmissionSources([]);
      });
  };

  const updateFactorsForSource = (sourceName, sourcesData = null) => {
    if (!fromDataPointPage || !sourceName) {
      setEmissionFactors([]);
      return;
    }
    const sourcesToUse = sourcesData || sourcesWithFactors;
    const selectedSourceData = sourcesToUse.find(s => s.value === sourceName);
    if (selectedSourceData && selectedSourceData.factors) {
      const factors = selectedSourceData.factors.map(factor => ({
        value: factor.id || `${factor.ghg}_${factor.efValue}`,
        display: `${factor.ghg} - ${factor.efValue}`,
        factorId: factor.id,
        ghg: factor.ghg,
        efValue: factor.efValue
      }));
      setEmissionFactors(factors);
    } else {
      setEmissionFactors([]);
    }
  };

  const handleLinkFromChange = (newLinkFrom) => {
    setLinkFrom(newLinkFrom);
    setSelectedVersion("");
    setSelectedSource("");
    setSelectedFactor("");
    setEmissionSources([]);
    setEmissionFactors([]);
    setSourcesWithFactors([]);
    if (selectedTable) {
      loadVersions(selectedTable, newLinkFrom);
    }
  };

  const handleTableChange = (newTable) => {
    setSelectedTable(newTable);
    setSelectedSource("");
    setSelectedFactor("");
    setSelectedVersion("");
    setEmissionSources([]);
    setEmissionFactors([]);
    setSourcesWithFactors([]);
    setVersions([]);

    if (fromDataPointPage && newTable) {
      loadVersions(newTable, linkFrom);
    } else if (newTable) {
      getEmissionSources(newTable);
    }
  };

  const handleVersionChange = (versionId) => {
    setSelectedVersion(versionId);
    setSelectedSource("");
    setSelectedFactor("");
    setEmissionFactors([]);
    if (versionId && selectedTable) {
      getEmissionSourcesByVersion(selectedTable, versionId);
    } else {
      setEmissionSources([]);
      setSourcesWithFactors([]);
    }
  };

  const handleSourceChange = (sourceName) => {
    setSelectedSource(sourceName);
    setSelectedFactor("");
    if (fromDataPointPage) {
      updateFactorsForSource(sourceName);
    }
  };

  const metricEmissionLinkingSave = () => {
    const payload = {
      name: selectedSource,
      database: selectedTable,
    };

    GHGEmissionService.saveMetricEmissionLinking(glbRegulationId, item.id, payload)
      .then((response) => {
        if (response.status === 200) {
          toast.success(response.data.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        if (onSaveSuccess) onSaveSuccess();
        handleClose();
      })
      .catch((error) => {
      });
  };

  const datapointEmissionLinkingSave = () => {
    const payload = {
      name: selectedSource,
      database: selectedTable,
      versionRef: selectedVersion,
      factorRef: selectedFactor,
    };

    GHGEmissionService.saveDatapointEmissionLinking(
      glbRegulationId,
      item.id,
      payload
    )
      .then((response) => {
        if (response.status === 200) {
          toast.success(response.data.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        if (onSaveSuccess) onSaveSuccess();
        handleClose();
      })
      .catch((error) => {
      });
  };

  const getLinkedEmissionMatric = () => {
    GHGEmissionService.getMetricEmissionLinking(glbRegulationId, item.id)
      .then((response) => {
        const data = response.data?.data || {};
        setSelectedTable(data.database || "");
        setSelectedSource(data.name || "");
        if (data.database) {
          getEmissionSources(data.database);
        }
      })
      .catch((error) => {
      });
  };

  const getLinkedEmissionDatapoint = () => {
    GHGEmissionService.getDatapointEmissionLinking(glbRegulationId, item.id)
      .then((response) => {
        const data = response.data?.data || {};
        const savedSource = data.name || "";
        const savedFactorRef = data.factorRef;
        setSelectedTable(data.database || "");
        setSelectedSource(savedSource);
        if (savedFactorRef) {
          setSelectedFactor(savedFactorRef);
        }
        if (data.versionRef && data.database) {
          GHGEmissionMasterDataService.listVersions(data.database, null, true).then(versionsRes => {
            if (versionsRes.status === 200) {
              const allVersions = Array.isArray(versionsRes.data) ? versionsRes.data : [versionsRes.data].filter(Boolean);
              const savedVersion = allVersions.find(v => v.id === data.versionRef);
              if (savedVersion) {
                const hasCompanyId = savedVersion.companyId && (companyId && savedVersion.companyId === companyId);
                const determinedLinkFrom = hasCompanyId ? 'company' : 'global';
                setLinkFrom(determinedLinkFrom);

                const fetchCompanyId = determinedLinkFrom === 'company' ? companyId : null;
                GHGEmissionMasterDataService.listVersions(data.database, fetchCompanyId).then(res => {
                  if (res.status === 200) {
                    const versionsList = Array.isArray(res.data) ? res.data : [res.data].filter(Boolean);
                    setVersions(versionsList);
                    setSelectedVersion(data.versionRef);
                    getEmissionSourcesByVersion(data.database, data.versionRef, (formattedSources) => {
                      if (savedSource) {
                        updateFactorsForSource(savedSource, formattedSources);
                      }
                    });
                  }
                }).catch(err => { });
              }
            }
          }).catch(err => { });
        }
      })
      .catch((error) => {
      });
  };

  const unlinkEmissionFactorNew = (id) => {
    if (fromDataPointPage) {
      GHGEmissionService.deleteDatapointEmissionLinking(glbRegulationId, id)
        .then((response) => {
          if (response.status === 200) {
            toast.success(response.data.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
            if (onSaveSuccess) onSaveSuccess();
            handleClose();
          }
        })
        .catch((error) => {
        });
    } else {
      GHGEmissionService.deleteMetricEmissionLinking(glbRegulationId, id)
        .then((response) => {
          if (response.status === 200) {
            toast.success(response.data.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
            if (onSaveSuccess) onSaveSuccess();
            handleClose();
          }
        })
        .catch((error) => {
        });
    }
  }

  return (
    <>
      <OverlayTrigger
        placement="top"
        overlay={Utils.renderTooltip(
          item.emissionLinked ? (
            <div style={{ textAlign: "left" }}>
              <label>Emission factor linked</label>
            </div>
          ) : (
            <label>No emission factor linked</label>
          )
        )}
      >
        <li>
          {item.emissionLinked
            ? t("EmissionFactorlinked.label")
            : t("NoEmissionFactorlinked.label")}

          <a
            data-testid="emission-factor-link-open"
            className={`ms-2 ${disabled ? 'disabled-icon' : ''}`}
            href="#"
            onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            handleClick();
          }} hidden={!hasConfigUpdatePermission() || disabled}>
            <FiLink2 />
          </a>

          {item.emissionLinked && (
            <a
              className={`ms-2 ${disabled ? 'disabled-icon' : ''}`}
              href="#"
              hidden={!hasConfigUpdatePermission() || disabled}
              onClick={e => {
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                e.preventDefault();
                setPendingUnlinkId(item.id);
                setShowConfirmModal(true);
              }}
            >
              <FiDelete />
            </a>
          )}
        </li>
      </OverlayTrigger>

      <Modal
        show={showModal}
        onHide={handleClose}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title as="h5">{item.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            noValidate
            validated={validated ? "true" : undefined}
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              if (form.checkValidity() === false) {
                e.stopPropagation();
                setValidated(true);
                return;
              }
              if (fromDataPointPage) {
                const sourceOk = selectedSource && String(selectedSource).trim() !== '';
                const factorOk = !!selectedFactor;
                if (!sourceOk || !factorOk) {
                  setValidated(true);
                  return;
                }
                datapointEmissionLinkingSave();
              } else {
                metricEmissionLinkingSave();
              }
            }}
          >
            {fromDataPointPage && (
              <div className="py-2">
                <Form.Label>
                  {t("LinkEmissionFactor.linkFrom.label")}
                  <span className="text-danger ms-1" aria-hidden="true">*</span>
                </Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label={t("LinkEmissionFactor.companyDatasets.label")}
                    name="linkFrom"
                    id="linkFromCompany"
                    checked={linkFrom === 'company'}
                    onChange={() => handleLinkFromChange('company')}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label={t("LinkEmissionFactor.globalDatasets.label")}
                    name="linkFrom"
                    id="linkFromGlobal"
                    checked={linkFrom === 'global'}
                    onChange={() => handleLinkFromChange('global')}
                  />
                </div>
              </div>
            )}
            <div className="py-2">
              <label className="mb-1">
                Emission Table
                <span className="text-danger ms-1" aria-hidden="true">*</span>
              </label>
              <select
                id="emissionTable"
                className={`form-select${isInvalid(selectedTable) ? " border-danger" : ""}`}
                required
                value={selectedTable}
                onChange={(e) => handleTableChange(e.target.value)}
              >
                <option value="">Select</option>
                {(emissionTableList || []).map((table) => (
                  <option key={table.id} value={table.name}>
                    {table.name}
                  </option>
                ))}
              </select>
            </div>
            {fromDataPointPage && (
              <div className="py-2">
                <label className="mb-1">
                  {t("Version.label")}
                  <span className="text-danger ms-1" aria-hidden="true">*</span>
                </label>
                <select
                  id="versionSelect"
                  className={`form-select${isInvalid(selectedVersion) ? " border-danger" : ""}`}
                  required
                  value={selectedVersion}
                  onChange={(e) => handleVersionChange(e.target.value)}
                >
                  <option value="">{t("Select.label")}</option>
                  {(versions || []).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.versionName || `Version ${v.versionNumber}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="py-2">
              <label className="mb-1">
                Emission Sources
                <span className="text-danger ms-1" aria-hidden="true">*</span>
              </label>
              <Typeahead
                id="emissionSources"
                key="emissionSources"
                clearButton
                labelKey={(option) => option.display || option.value || option}
                selected={(emissionSources || []).filter(src => (src.value || src) === selectedSource)}
                onChange={(selected) => {
                  const val = selected.length > 0 ? (selected[0].value || selected[0]) : '';
                  handleSourceChange(val);
                }}
                options={emissionSources || []}
                placeholder={t ? t("Search.label") : "Search"}
                inputProps={{
                  'data-testid': 'emission-source-typeahead',
                  'aria-required': fromDataPointPage ? 'true' : undefined,
                  className: isInvalid(selectedSource) ? 'border-danger' : undefined,
                }}
              />
            </div>
            {fromDataPointPage && selectedSource && (
              <div className="py-2">
                <label className="mb-1">
                  {t("EmissionFactor.label")}
                  <span className="text-danger ms-1" aria-hidden="true">*</span>
                </label>
                <Typeahead
                  id="emissionFactor"
                  key="emissionFactor"
                  clearButton
                  labelKey={(option) => option.display || option.value}
                  selected={(emissionFactors || []).filter(f => f.value === selectedFactor)}
                  onChange={(selected) => {
                    const val = selected.length > 0 ? selected[0].value : '';
                    setSelectedFactor(val);
                  }}
                  options={emissionFactors || []}
                  placeholder={t ? t("Search.label") : "Search"}
                  inputProps={{
                    'data-testid': 'emission-factor-typeahead',
                    'aria-required': 'true',
                    className: isInvalid(selectedFactor) ? 'border-danger' : undefined,
                  }}
                />
              </div>
            )}
            <Modal.Footer>
              <Button variant="primary" type="submit">
                {item.emissionLinked ? "Update" : "Save"}
              </Button>
              <Button variant="secondary" onClick={handleClose}>
                Discard
              </Button>
            </Modal.Footer>
          </form>
        </Modal.Body>
      </Modal>
      {showConfirmModal && (
        <ConfirmationModal
          modalShow={showConfirmModal}
          messageText={t("UnlinkEmissionFactorConfirmation.label")}
          callbackModal={(confirmed) => {
            setShowConfirmModal(false);
            if (confirmed && pendingUnlinkId) {
              unlinkEmissionFactorNew(pendingUnlinkId);
            }
            setPendingUnlinkId(null);
          }}
          confirmBtn={t("Confirm.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}
    </>
  );
}

export default withTranslation()(EmissionFactorLinker);
